import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, TextField, TextFieldProps } from '@mui/material';
import { IconButton, colors } from '@sweep-io/sweep-design';
import { ArrowUp } from '@sweep-io/sweep-design/dist/icons';
import classNames from 'classnames';
import SelectedTextBox from './SelectedTextBox';

const StopIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect
      x="0.5"
      y="0.5"
      width="11"
      height="11"
      rx="2"
      fill="white"
      style={{ fill: 'white', fillOpacity: 1 }}
    />
  </svg>
);

type TextFieldWithButtonV2Props = TextFieldProps & {
  onConfirm: (message: string) => Promise<void>;
  inputPlaceholder?: string;
  readonly?: boolean;
  isLoading?: boolean;
  value: string;
  setValue: (value: string) => void;
  selectedText?: string | null;
  onClearSelectedText?: () => void;
  maxInputHeight?: number;
  renderStopButton?: boolean;
  onStopClick?: () => void;
  leftAdornment?: React.ReactNode;
  leftAdornmentOnSeparateLine?: boolean;
};

// Markdown requires double-space before newline
const handleNewlines = (input: string) => input.replace(/\n/g, '  \n');

export const TextFieldWithButtonV2 = ({
  value,
  setValue,
  onConfirm,
  onFocus,
  onBlur,
  inputPlaceholder,
  readonly = false,
  isLoading = false,
  selectedText,
  onClearSelectedText,
  maxInputHeight,
  renderStopButton = false,
  onStopClick,
  leftAdornment,
  leftAdornmentOnSeparateLine = false,
  ...rest
}: TextFieldWithButtonV2Props) => {
  const isValid = !!value;
  const inputRef = useRef<HTMLInputElement>(null);
  const [focused, setFocused] = useState(false);

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (!isLoading && event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      if (!renderStopButton) {
        _onConfirm();
      }
    }
  };

  const _onConfirm = useCallback(async () => {
    if (!isValid) return;
    const messageToSend = handleNewlines(value);
    setValue('');
    await onConfirm(messageToSend);
  }, [isValid, onConfirm, value, setValue]);

  const handleClearSelectedText = useCallback(() => {
    onClearSelectedText?.();
  }, [onClearSelectedText]);

  // Return focus to input after server response
  useEffect(() => {
    if (!isLoading && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isLoading]);

  // Return focus to input after adding reply
  useEffect(() => {
    if (selectedText && inputRef.current) {
      inputRef.current.focus();
    }
  }, [selectedText]);

  const containerStyles = {
    width: '100%',
    borderRadius: '28px',
    padding: '12px',
    boxShadow: '0px 0px 18px 0px rgba(0, 0, 0, 0.03)',
    border: `1px solid ${focused ? colors.blue[500] : colors.grey[300]}`,
    display: 'flex',
    flexDirection: 'column',
    gap: 1,
    backgroundColor: colors.white,
  };

  // Common TextField props used across all layouts
  const commonTextFieldProps = {
    ...rest,
    inputRef,
    onKeyDown: handleKeyDown,
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => setValue(event.target.value),
    onFocus: (event: React.FocusEvent<HTMLInputElement>) => {
      setFocused(true);
      onFocus?.(event);
    },
    onBlur: (event: React.FocusEvent<HTMLInputElement>) => {
      setFocused(false);
      onBlur?.(event);
    },
    value,
    multiline: true,
    className: classNames({ focused }),
    sx: {
      width: '100%',
      '& .MuiOutlinedInput-root.Mui-disabled': {
        background: 'none',
      },
      '& .MuiOutlinedInput-root.MuiInputBase-multiline': {
        padding: (!leftAdornment || leftAdornmentOnSeparateLine) ? '0 8px' : '0',
        minHeight: '32px',
        '& fieldset': {
          border: 'none',
        },
      },
      '& .MuiOutlinedInput-input': {
        padding: '0',
        minHeight: '20px',
      },
    },
    InputProps: {
      placeholder: inputPlaceholder,
      disabled: readonly || isLoading,
      sx: {
        ...(maxInputHeight && {
          '& textarea': {
            maxHeight: `${maxInputHeight}px !important`,
            overflowY: 'scroll !important',
            resize: 'none',
          },
        }),
      },
    },
  };

  const sendButton = (
    <Box
      sx={{
        '.MuiButton-root:disabled': {
          borderColor: colors.blue[300],
          backgroundColor: colors.blue[300],
          'svg path': {
            stroke: colors.white,
          },
          '.MuiCircularProgress-root': {
            color: colors.white,
          },
        },
      }}
    >
      {renderStopButton ? (
        <IconButton
          size="small"
          variant="primary"
          onClick={onStopClick}
          disabled={readonly}
          loading={isLoading}
        >
          <StopIcon />
        </IconButton>
      ) : (
        <IconButton
          size="small"
          variant="primary"
          onClick={_onConfirm}
          loading={isLoading}
          disabled={isLoading || readonly || !isValid}
        >
          <ArrowUp />
        </IconButton>
      )}
    </Box>
  );



  // Layout 1: Three-row layout (textarea on its own row)
  // Row 1: Selected text (if any)
  // Row 2: Textarea (full width)
  // Row 3: [leftAdornment] ... [send button]
  if (leftAdornmentOnSeparateLine && leftAdornment) {
    return (
      <Box sx={{ ...containerStyles }}>
        {selectedText && (
          <SelectedTextBox selectedText={selectedText} onCloseClick={handleClearSelectedText} />
        )}

        <TextField {...commonTextFieldProps} />

        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            {leftAdornment}
          </Box>
          {sendButton}
        </Box>
      </Box>
    );
  }

  // Default Layout: Two-row layout with inline leftAdornment (if any)
  // Row 1: Selected text (if any)
  // Row 2: [leftAdornment] [textarea] [send button]
  return (
    <Box sx={{ ...containerStyles }}>
      {selectedText && (
        <SelectedTextBox selectedText={selectedText} onCloseClick={handleClearSelectedText} />
      )}

      <Box display="flex" alignItems="end" gap={1}>
        {leftAdornment}
        <TextField {...commonTextFieldProps} />
        {sendButton}
      </Box>
    </Box>
  );
};
